import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  X, Play, Pause, Check, SkipForward, Maximize2, Minimize2,
  Volume2, VolumeX, ChevronRight
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import useWorkoutSounds from '../../hooks/useWorkoutSounds';
import useFullscreen from '../../hooks/useFullscreen';

const ActiveWorkoutMode = ({ 
  workout, 
  language, 
  onComplete, 
  onExit 
}) => {
  const containerRef = useRef(null);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [phase, setPhase] = useState('countdown'); // countdown, exercise, rest, complete
  const [countdownValue, setCountdownValue] = useState(3);
  const [exerciseTimer, setExerciseTimer] = useState(0);
  const [restTimer, setRestTimer] = useState(0);
  const [totalTimer, setTotalTimer] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [completedExercises, setCompletedExercises] = useState(new Set());

  const { isFullscreen, toggleFullscreen } = useFullscreen(containerRef);
  const sounds = useWorkoutSounds();

  const currentExercise = workout?.exercises?.[currentExerciseIndex];
  const totalExercises = workout?.exercises?.length || 0;
  const progress = totalExercises > 0 ? ((completedExercises.size) / totalExercises) * 100 : 0;

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Initial countdown effect
  useEffect(() => {
    if (phase !== 'countdown' || isPaused) return;

    if (countdownValue > 0) {
      if (soundEnabled) sounds.playBeep(600, 0.15, 0.3);
      
      const timer = setTimeout(() => {
        setCountdownValue(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      // Countdown finished, start exercise
      if (soundEnabled) sounds.playExerciseStart();
      setPhase('exercise');
    }
  }, [phase, countdownValue, isPaused, soundEnabled, sounds]);

  // Exercise timer effect
  useEffect(() => {
    if (phase !== 'exercise' || isPaused) return;

    const timer = setInterval(() => {
      setExerciseTimer(prev => prev + 1);
      setTotalTimer(prev => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [phase, isPaused]);

  // Rest timer effect
  useEffect(() => {
    if (phase !== 'rest' || isPaused) return;

    if (restTimer > 0) {
      // Play warning beeps in last 3 seconds
      if (restTimer <= 3 && soundEnabled) {
        sounds.playRestWarning();
      }

      const timer = setTimeout(() => {
        setRestTimer(prev => prev - 1);
        setTotalTimer(prev => prev + 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      // Rest finished, next exercise or complete
      if (currentExerciseIndex < totalExercises - 1) {
        if (soundEnabled) sounds.playRestComplete();
        setCurrentExerciseIndex(prev => prev + 1);
        setExerciseTimer(0);
        setPhase('exercise');
      } else {
        handleWorkoutComplete();
      }
    }
  }, [phase, restTimer, isPaused, currentExerciseIndex, totalExercises, soundEnabled, sounds]);

  const handleCompleteExercise = useCallback(() => {
    if (soundEnabled) sounds.playExerciseComplete();
    
    const newCompleted = new Set(completedExercises);
    newCompleted.add(currentExerciseIndex);
    setCompletedExercises(newCompleted);

    if (currentExerciseIndex < totalExercises - 1) {
      // Start rest period
      const restTime = currentExercise?.rest_seconds || 30;
      setRestTimer(restTime);
      setPhase('rest');
    } else {
      // Last exercise completed
      handleWorkoutComplete();
    }
  }, [currentExerciseIndex, totalExercises, currentExercise, soundEnabled, sounds, completedExercises]);

  const handleSkipExercise = useCallback(() => {
    if (currentExerciseIndex < totalExercises - 1) {
      setCurrentExerciseIndex(prev => prev + 1);
      setExerciseTimer(0);
      setPhase('exercise');
    } else {
      handleWorkoutComplete();
    }
  }, [currentExerciseIndex, totalExercises]);

  const handleWorkoutComplete = useCallback(() => {
    if (soundEnabled) sounds.playWorkoutComplete();
    setPhase('complete');
    
    setTimeout(() => {
      onComplete({
        duration_minutes: Math.ceil(totalTimer / 60),
        calories_burned: workout?.calories_estimate || 200
      });
    }, 2000);
  }, [soundEnabled, sounds, totalTimer, workout, onComplete]);

  const handleExit = () => {
    if (isFullscreen) {
      document.exitFullscreen?.();
    }
    onExit();
  };

  // Calculate circular progress for timer
  const getCircleProgress = () => {
    if (phase === 'countdown') {
      return ((3 - countdownValue) / 3) * 100;
    }
    if (phase === 'rest') {
      const totalRest = currentExercise?.rest_seconds || 30;
      return ((totalRest - restTimer) / totalRest) * 100;
    }
    return 0;
  };

  const circleProgress = getCircleProgress();
  const circumference = 2 * Math.PI * 120; // radius = 120

  return (
    <div 
      ref={containerRef}
      className={`fixed inset-0 z-50 bg-[#0a1515] flex flex-col ${isFullscreen ? 'p-8' : ''}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-800/50">
        <button
          onClick={handleExit}
          className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors"
        >
          <X className="w-6 h-6" />
          <span className="text-sm">{language === 'en' ? 'Exit' : 'Salir'}</span>
        </button>

        <div className="text-center">
          <p className="text-xs text-gray-500 uppercase tracking-wider">
            {language === 'en' ? 'Total Time' : 'Tiempo Total'}
          </p>
          <p className="text-xl font-mono font-bold text-white">{formatTime(totalTimer)}</p>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={() => setSoundEnabled(!soundEnabled)}
            className="p-2 text-gray-400 hover:text-white transition-colors"
          >
            {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
          </button>
          <button
            onClick={toggleFullscreen}
            className="p-2 text-gray-400 hover:text-white transition-colors"
          >
            {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center p-4 overflow-hidden">
        {/* Countdown Phase */}
        {phase === 'countdown' && (
          <div className="text-center animate-pulse">
            <div className="relative w-64 h-64 mx-auto mb-8">
              <svg className="w-full h-full transform -rotate-90">
                <circle
                  cx="128"
                  cy="128"
                  r="120"
                  stroke="currentColor"
                  strokeWidth="8"
                  fill="none"
                  className="text-gray-800"
                />
                <circle
                  cx="128"
                  cy="128"
                  r="120"
                  stroke="currentColor"
                  strokeWidth="8"
                  fill="none"
                  strokeDasharray={circumference}
                  strokeDashoffset={circumference - (circleProgress / 100) * circumference}
                  strokeLinecap="round"
                  className="text-emerald-500 transition-all duration-300"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-8xl font-bold text-white">
                  {countdownValue || language === 'en' ? 'GO!' : '¡YA!'}
                </span>
              </div>
            </div>
            <p className="text-2xl text-gray-400">
              {language === 'en' ? 'Get Ready!' : '¡Prepárate!'}
            </p>
          </div>
        )}

        {/* Exercise Phase */}
        {phase === 'exercise' && currentExercise && (
          <div className="w-full max-w-2xl text-center">
            {/* Exercise Number */}
            <div className="mb-4">
              <span className="bg-emerald-500 text-white text-sm font-bold px-4 py-2 rounded-full">
                {currentExerciseIndex + 1} / {totalExercises}
              </span>
            </div>

            {/* Timer Circle */}
            <div className="relative w-48 h-48 mx-auto mb-6">
              <svg className="w-full h-full transform -rotate-90">
                <circle
                  cx="96"
                  cy="96"
                  r="88"
                  stroke="currentColor"
                  strokeWidth="6"
                  fill="none"
                  className="text-gray-800"
                />
                <circle
                  cx="96"
                  cy="96"
                  r="88"
                  stroke="currentColor"
                  strokeWidth="6"
                  fill="none"
                  strokeDasharray={2 * Math.PI * 88}
                  strokeDashoffset={0}
                  strokeLinecap="round"
                  className="text-emerald-500"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-4xl font-mono font-bold text-white">
                  {formatTime(exerciseTimer)}
                </span>
              </div>
            </div>

            {/* Exercise Name */}
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
              {language === 'en' ? currentExercise.name_en : currentExercise.name_es}
            </h2>

            {/* Sets/Reps Info */}
            <p className="text-xl text-emerald-400 mb-6">
              {currentExercise.sets && `${currentExercise.sets} ${language === 'en' ? 'sets' : 'series'}`}
              {currentExercise.reps && ` × ${currentExercise.reps} reps`}
              {currentExercise.duration_seconds && `${currentExercise.duration_seconds}s`}
            </p>

            {/* Action Buttons */}
            <div className="flex items-center justify-center space-x-4">
              <Button
                onClick={() => setIsPaused(!isPaused)}
                variant="outline"
                size="lg"
                className="w-16 h-16 rounded-full border-gray-600 hover:bg-gray-800"
              >
                {isPaused ? <Play className="w-6 h-6" /> : <Pause className="w-6 h-6" />}
              </Button>

              <Button
                onClick={handleCompleteExercise}
                size="lg"
                className="h-16 px-10 bg-emerald-500 hover:bg-emerald-600 text-xl font-semibold rounded-full"
              >
                <Check className="w-6 h-6 mr-2" />
                {language === 'en' ? 'Done' : 'Listo'}
              </Button>

              <Button
                onClick={handleSkipExercise}
                variant="outline"
                size="lg"
                className="w-16 h-16 rounded-full border-gray-600 hover:bg-gray-800"
              >
                <SkipForward className="w-6 h-6" />
              </Button>
            </div>
          </div>
        )}

        {/* Rest Phase */}
        {phase === 'rest' && (
          <div className="text-center">
            <div className="relative w-64 h-64 mx-auto mb-8">
              <svg className="w-full h-full transform -rotate-90">
                <circle
                  cx="128"
                  cy="128"
                  r="120"
                  stroke="currentColor"
                  strokeWidth="8"
                  fill="none"
                  className="text-gray-800"
                />
                <circle
                  cx="128"
                  cy="128"
                  r="120"
                  stroke="currentColor"
                  strokeWidth="8"
                  fill="none"
                  strokeDasharray={circumference}
                  strokeDashoffset={circumference - (circleProgress / 100) * circumference}
                  strokeLinecap="round"
                  className="text-blue-500 transition-all duration-1000"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-6xl font-mono font-bold text-white">
                  {restTimer}
                </span>
                <span className="text-sm text-gray-400 uppercase tracking-wider mt-2">
                  {language === 'en' ? 'seconds' : 'segundos'}
                </span>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-blue-400 mb-4">
              {language === 'en' ? 'Rest' : 'Descanso'}
            </h2>

            {/* Next Exercise Preview */}
            {currentExerciseIndex < totalExercises - 1 && (
              <div className="bg-gray-900/50 rounded-2xl p-4 border border-gray-800 max-w-md mx-auto">
                <p className="text-sm text-gray-500 mb-2">
                  {language === 'en' ? 'Next up' : 'Siguiente'}
                </p>
                <div className="flex items-center justify-center space-x-2">
                  <ChevronRight className="w-5 h-5 text-emerald-500" />
                  <span className="text-lg text-white font-medium">
                    {language === 'en' 
                      ? workout.exercises[currentExerciseIndex + 1]?.name_en 
                      : workout.exercises[currentExerciseIndex + 1]?.name_es}
                  </span>
                </div>
              </div>
            )}

            {/* Skip Rest Button */}
            <Button
              onClick={() => {
                setRestTimer(0);
              }}
              variant="ghost"
              className="mt-6 text-gray-400 hover:text-white"
            >
              {language === 'en' ? 'Skip Rest' : 'Saltar Descanso'}
            </Button>
          </div>
        )}

        {/* Complete Phase */}
        {phase === 'complete' && (
          <div className="text-center animate-bounce">
            <div className="w-32 h-32 mx-auto mb-8 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
              <Check className="w-16 h-16 text-white" />
            </div>
            <h2 className="text-4xl font-bold text-white mb-4">
              {language === 'en' ? 'Workout Complete!' : '¡Entrenamiento Completado!'}
            </h2>
            <p className="text-xl text-gray-400">
              {language === 'en' ? 'Great job!' : '¡Excelente trabajo!'}
            </p>
          </div>
        )}
      </div>

      {/* Progress Bar */}
      <div className="p-4 border-t border-gray-800/50">
        <div className="max-w-2xl mx-auto">
          <div className="flex justify-between text-sm text-gray-500 mb-2">
            <span>{language === 'en' ? 'Progress' : 'Progreso'}</span>
            <span>{completedExercises.size} / {totalExercises}</span>
          </div>
          <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActiveWorkoutMode;
